import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { Heatmap } from '../components/Heatmap';
import { type ApiReviewSummary, useCardHistory, useReviewSummary } from '../lib/queries';

// Stats dashboard: retention, daily throughput, maturity mix, per-deck table,
// leech list (with drill-down), and the same forecast heatmap the library has.
export function StatsRoute() {
  const { t } = useTranslation('stats');
  const summaryQuery = useReviewSummary();
  const summary = summaryQuery.data;
  const [inspectCardId, setInspectCardId] = useState<string | null>(null);

  return (
    <div style={{ padding: '32px 40px 60px' }}>
      <header
        style={{
          paddingBottom: 20,
          marginBottom: 32,
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <div
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.ink3,
            marginBottom: 10,
          }}
        >
          {t('overline')}
        </div>
        <h1
          style={{
            fontWeight: 600,
            letterSpacing: '-0.02em',
            fontSize: 44,
            lineHeight: 1,
            margin: 0,
          }}
        >
          {t('title')}
        </h1>
      </header>

      <TopRow summary={summary} />

      <Section title={t('throughput')} hint={t('throughput_hint')}>
        <DailyChart days={summary?.daily30d ?? []} />
      </Section>

      <Section title={t('maturity')} hint={t('maturity_hint')}>
        <MaturityBar
          newCount={summary?.totalNew ?? 0}
          learning={summary?.totalLearning ?? 0}
          young={summary?.youngCount ?? 0}
          mature={summary?.matureCount ?? 0}
        />
      </Section>

      <Section title={t('per_deck')}>
        <DeckTable decks={summary?.perDeckStats ?? []} />
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 32 }}>
        <Section title={t('leeches')} hint={t('leeches_hint')}>
          <LeechList
            leeches={summary?.leeches ?? []}
            onInspect={setInspectCardId}
          />
        </Section>
        <Section title={t('forecast')}>
          <Heatmap />
        </Section>
      </div>

      {inspectCardId && (
        <CardHistoryDrawer cardId={inspectCardId} onClose={() => setInspectCardId(null)} />
      )}
    </div>
  );
}

function TopRow({ summary }: { summary: ApiReviewSummary | undefined }) {
  const { t } = useTranslation('stats');
  const retention = summary?.retention30d ?? 0;
  const pct = Math.round(retention * 100);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 32,
      }}
    >
      <BigStat
        label={t('retention_30d')}
        value={summary ? `${pct}%` : '…'}
        tone={pct >= 85 ? colors.good : pct >= 70 ? colors.warn : colors.bad}
        hint={summary ? t('retention_reviews', { count: summary.daily30d.reduce((s, d) => s + d.reviewed, 0) }) : ''}
      />
      <BigStat
        label={t('streak')}
        value={summary ? String(summary.streak) : '…'}
        tone={colors.accent}
        hint={summary && summary.streak > 0 ? t('streak_days') : t('streak_none')}
      />
      <BigStat
        label={t('studied_30d')}
        value={summary ? String(summary.studied30d) : '…'}
        tone={colors.ink2}
        hint={t('studied_cards')}
      />
      <BigStat
        label={t('upcoming_load')}
        value={summary ? String(summary.forecast.reduce((s, d) => s + d.count, 0)) : '…'}
        tone={colors.ink}
        hint={t('upcoming_hint')}
      />
    </div>
  );
}

function BigStat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        background: colors.surface2,
        border: `1px solid ${colors.line}`,
        borderRadius: 10,
        padding: '16px 18px 18px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: colors.ink3,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 36,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
          color: tone,
          marginTop: 6,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11.5, color: colors.ink3, marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.015em', margin: 0 }}>
          {title}
        </h3>
        {hint && <span style={{ fontSize: 11.5, color: colors.ink3 }}>{hint}</span>}
      </div>
      <div
        style={{
          background: colors.surface2,
          border: `1px solid ${colors.line}`,
          borderRadius: 10,
          padding: '18px 20px',
        }}
      >
        {children}
      </div>
    </section>
  );
}

function DailyChart({ days }: { days: { day: string; reviewed: number; again: number }[] }) {
  const max = useMemo(() => Math.max(1, ...days.map((d) => d.reviewed)), [days]);
  if (days.length === 0) {
    return <div style={{ color: colors.ink3, fontSize: 13 }}>—</div>;
  }
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${days.length}, 1fr)`,
          gap: 3,
          alignItems: 'end',
          height: 140,
        }}
      >
        {days.map((d) => {
          const h = (d.reviewed / max) * 100;
          const againH = d.reviewed > 0 ? (d.again / d.reviewed) * h : 0;
          return (
            <div
              key={d.day}
              title={`${d.day}: ${d.reviewed} reviewed · ${d.again} again`}
              style={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              <div
                style={{
                  height: `${h}%`,
                  background: colors.accent,
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                {againH > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `${(againH / h) * 100}%`,
                      background: colors.bad,
                      borderRadius: 2,
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 10,
          color: colors.ink3,
          marginTop: 8,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{days[0]?.day}</span>
        <span style={{ display: 'flex', gap: 12 }}>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: colors.accent,
                marginRight: 4,
                borderRadius: 1,
                verticalAlign: 'middle',
              }}
            />
            reviewed
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: colors.bad,
                marginRight: 4,
                borderRadius: 1,
                verticalAlign: 'middle',
              }}
            />
            again
          </span>
        </span>
        <span>{days[days.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function MaturityBar({
  newCount,
  learning,
  young,
  mature,
}: {
  newCount: number;
  learning: number;
  young: number;
  mature: number;
}) {
  const total = newCount + learning + young + mature;
  const segs = [
    { key: 'mature', value: mature, color: colors.good, label: 'mature' },
    { key: 'young', value: young, color: colors.accent, label: 'young' },
    { key: 'learning', value: learning, color: colors.warn, label: 'learning' },
    { key: 'new', value: newCount, color: colors.ink3, label: 'new' },
  ];
  if (total === 0) {
    return <div style={{ color: colors.ink3, fontSize: 13 }}>—</div>;
  }
  return (
    <div>
      <div
        style={{
          display: 'flex',
          height: 18,
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${colors.line2}`,
        }}
      >
        {segs.map((s) => {
          const pct = (s.value / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={s.key}
              title={`${s.label}: ${s.value} (${Math.round(pct)}%)`}
              style={{ width: `${pct}%`, background: s.color }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        {segs.map((s) => (
          <span key={s.key} style={{ fontSize: 12, color: colors.ink2 }}>
            <span
              style={{
                display: 'inline-block',
                width: 9,
                height: 9,
                background: s.color,
                borderRadius: 2,
                marginRight: 6,
                verticalAlign: 'middle',
              }}
            />
            <strong style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {s.value}
            </strong>{' '}
            <span style={{ color: colors.ink3 }}>{s.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function DeckTable({ decks }: { decks: ApiReviewSummary['perDeckStats'] }) {
  const { t } = useTranslation('stats');
  if (decks.length === 0) {
    return <div style={{ color: colors.ink3, fontSize: 13 }}>{t('no_decks')}</div>;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
        gap: 0,
        fontSize: 13,
      }}
    >
      <HeaderCell>{t('col_deck')}</HeaderCell>
      <HeaderCell align="right">{t('col_retention')}</HeaderCell>
      <HeaderCell align="right">{t('col_reviews')}</HeaderCell>
      <HeaderCell align="right">{t('col_mature')}</HeaderCell>
      <HeaderCell align="right">{t('col_young')}</HeaderCell>
      {decks.map((d) => (
        <DeckRow key={d.pageId} deck={d} />
      ))}
    </div>
  );
}

function HeaderCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <div
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.ink3,
        padding: '6px 10px',
        textAlign: align,
        borderBottom: `1px solid ${colors.line}`,
      }}
    >
      {children}
    </div>
  );
}

function DeckRow({ deck }: { deck: ApiReviewSummary['perDeckStats'][number] }) {
  const pct = deck.retention == null ? null : Math.round(deck.retention * 100);
  const tone = pct == null
    ? colors.ink3
    : pct >= 85
    ? colors.good
    : pct >= 70
    ? colors.warn
    : colors.bad;
  return (
    <>
      <Cell>
        <Link
          to={`/review/${deck.pageId}`}
          style={{ color: colors.ink, textDecoration: 'none' }}
        >
          {deck.title}
        </Link>
      </Cell>
      <Cell align="right" tone={tone}>
        {pct == null ? '—' : `${pct}%`}
      </Cell>
      <Cell align="right">{deck.reviews}</Cell>
      <Cell align="right">{deck.mature}</Cell>
      <Cell align="right">{deck.young}</Cell>
    </>
  );
}

function Cell({
  children,
  align = 'left',
  tone,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  tone?: string;
}) {
  return (
    <div
      style={{
        padding: '9px 10px',
        borderBottom: `1px solid ${colors.line}`,
        textAlign: align,
        color: tone ?? colors.ink,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </div>
  );
}

function LeechList({
  leeches,
  onInspect,
}: {
  leeches: ApiReviewSummary['leeches'];
  onInspect: (cardId: string) => void;
}) {
  const { t } = useTranslation('stats');
  if (leeches.length === 0) {
    return <div style={{ color: colors.ink3, fontSize: 13 }}>{t('no_leeches')}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {leeches.map((l) => (
        <button
          key={l.cardId}
          onClick={() => onInspect(l.cardId)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '9px 4px',
            background: 'transparent',
            border: 'none',
            borderBottom: `1px solid ${colors.line}`,
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'left',
            color: colors.ink,
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.bad,
              background: 'rgba(232,69,69,0.1)',
              padding: '2px 7px',
              borderRadius: 3,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            {l.lapses} lapses
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {l.front}
            </div>
            <div style={{ fontSize: 11, color: colors.ink3, marginTop: 2 }}>
              {l.pageTitle}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function CardHistoryDrawer({ cardId, onClose }: { cardId: string; onClose: () => void }) {
  const { t } = useTranslation('stats');
  const historyQuery = useCardHistory(cardId);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(24,22,15,0.32)',
          zIndex: 400,
        }}
      />
      <aside
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 440,
          background: colors.surface,
          borderLeft: `1px solid ${colors.line}`,
          boxShadow: '-20px 0 40px -24px rgba(24,22,15,0.2)',
          zIndex: 500,
          overflowY: 'auto',
          padding: '28px 28px 40px',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: 0,
            marginLeft: 'auto',
            display: 'block',
            background: 'transparent',
            border: 'none',
            fontSize: 18,
            color: colors.ink3,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ×
        </button>

        {!historyQuery.data ? (
          <div style={{ color: colors.ink3, fontSize: 13 }}>…</div>
        ) : (
          <>
            <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.ink3 }}>
              {historyQuery.data.card.type} card
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, margin: '6px 0 18px' }}>
              {historyQuery.data.card.front}
            </h3>
            {historyQuery.data.state && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  fontSize: 12,
                  marginBottom: 22,
                  padding: '12px 14px',
                  background: colors.surface2,
                  borderRadius: 8,
                  border: `1px solid ${colors.line}`,
                }}
              >
                <StateRow label="State" value={historyQuery.data.state.state} />
                <StateRow label="Interval" value={`${historyQuery.data.state.intervalDays}d`} />
                <StateRow label="Ease" value={historyQuery.data.state.ease.toFixed(2)} />
                <StateRow label="Stability" value={historyQuery.data.state.stability.toFixed(2)} />
                <StateRow label="Difficulty" value={historyQuery.data.state.difficulty.toFixed(2)} />
                <StateRow label="Lapses" value={String(historyQuery.data.state.lapses)} />
                <StateRow label="Reps" value={String(historyQuery.data.state.reps)} />
                <StateRow
                  label="Due"
                  value={new Date(historyQuery.data.state.dueAt).toLocaleDateString()}
                />
              </div>
            )}

            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: colors.ink3,
                marginBottom: 10,
              }}
            >
              {t('history')}
            </div>
            {historyQuery.data.history.length === 0 ? (
              <div style={{ fontSize: 13, color: colors.ink3 }}>{t('no_history')}</div>
            ) : (
              historyQuery.data.history.map((h) => (
                <HistoryRow key={h.id} row={h} />
              ))
            )}
          </>
        )}
      </aside>
    </>
  );
}

function StateRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: colors.ink3 }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

const ratingTone: Record<string, string> = {
  again: colors.bad,
  hard: colors.warn,
  good: colors.ink,
  easy: colors.good,
};

function HistoryRow({
  row,
}: {
  row: {
    rating: string;
    reviewedAt: string;
    intervalDays: number;
    elapsedMs: number | null;
    undone: string | null;
  };
}) {
  const tone = ratingTone[row.rating] ?? colors.ink;
  const when = new Date(row.reviewedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0',
        borderBottom: `1px solid ${colors.line}`,
        opacity: row.undone ? 0.5 : 1,
      }}
    >
      <span
        style={{
          fontSize: 9,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tone,
          background: `${tone}1a`,
          padding: '3px 7px',
          borderRadius: 3,
          fontWeight: 500,
          width: 56,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {row.rating}
      </span>
      <span style={{ fontSize: 12, color: colors.ink2, flex: 1 }}>{when}</span>
      <span style={{ fontSize: 11, color: colors.ink3, fontVariantNumeric: 'tabular-nums' }}>
        → {row.intervalDays}d
      </span>
      {row.elapsedMs != null && (
        <span style={{ fontSize: 11, color: colors.ink3, fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(row.elapsedMs / 1000)}s
        </span>
      )}
      {row.undone && (
        <span style={{ fontSize: 10, color: colors.ink3, fontStyle: 'italic' }}>undone</span>
      )}
    </div>
  );
}
